<template>
  <div class="panel-auto-scroll">
    <div
      ref="viewport"
      class="panel-auto-scroll__viewport"
      @wheel.passive="onInteraction"
      @touchstart.passive="onInteraction"
      @pointerdown="onInteraction"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import {
  PAGE_DWELL_MS,
  PAGE_TRANSITION_MS,
  RESUME_AFTER_IDLE_MS,
  easeInOutCubic,
  findPageIndex,
  measurePageOffsets,
} from '@/domain/services/generalPanel/communicationStatusPanel/autoScroll';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * Rolagem Automática da Grade, por páginas. É o próprio container que rola:
 * a grade entra pelo slot.
 *
 * Só roda com `enabled` **e** com conteúdo passando da janela. Ver CONTEXT.md,
 * Rolagem Automática.
 */
export default {
  name: 'PanelAutoScroll',
  props: {
    /** Ligada pelo operador. Mesmo ligada, sem rolagem não faz nada. */
    enabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Muda quando a grade muda de forma — novo ciclo, filtro, agrupamento. As
     * páginas são medidas de novo, sem reiniciar a pausa em curso.
     */
    layoutKey: {
      type: [String, Number],
      default: '',
    },
    dwellMs: {
      type: Number,
      default: PAGE_DWELL_MS,
    },
  },
  data: () => ({
    offsets: [0],
    pageIndex: 0,
    paused: false,
    // Recria a barra de progresso a cada página, reiniciando a animação.
    cycleKey: 0,
  }),
  computed: {
    pageCount() {
      return this.offsets.length;
    },
    running() {
      return this.enabled && this.pageCount > 1;
    },
    /**
     * O que o Placar precisa para desenhar o selo de página, logo abaixo do
     * botão de Rolagem Automática. O selo mora lá, e não aqui, para ficar junto
     * do controle que liga a rolagem.
     */
    status() {
      return {
        running: this.running,
        paused: this.paused,
        current: this.pageIndex + 1,
        total: this.pageCount,
        cycleKey: this.cycleKey,
        dwellMs: this.dwellMs,
      };
    },
  },
  watch: {
    status: {
      immediate: true,
      handler(value) {
        this.$emit('status', value);
      },
    },
    enabled() {
      this.restart();
    },
    layoutKey() {
      this.$nextTick(() => this.remeasure());
    },
  },
  created() {
    // Fora do `data`: timers e quadro de animação não são estado de tela.
    this.dwellTimer = null;
    this.resumeTimer = null;
    this.frame = null;
    this.startedAt = null;
  },
  mounted() {
    window.addEventListener('resize', this.remeasure);
    this.restart();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.remeasure);
    this.stopTimers();
    // Trocar para o Radial ou esvaziar a grade com um filtro destrói a rolagem:
    // sem este aviso, o selo do Placar ficaria rodando sem nada rolar.
    this.$emit('status', { ...this.status, running: false, paused: false });
  },
  methods: {
    getScrollTop() {
      return this.$refs.viewport ? this.$refs.viewport.scrollTop : 0;
    },
    setScrollTop(value) {
      if (this.$refs.viewport) {
        this.$refs.viewport.scrollTop = value;
      }
    },
    /**
     * Mede de novo e se reposiciona sem interromper o que está em curso: a
     * pausa continua contando, e a página passa a ser a da posição atual.
     */
    remeasure() {
      this.offsets = measurePageOffsets(this.$refs.viewport);
      this.pageIndex = findPageIndex(this.offsets, this.getScrollTop());

      if (!this.running) {
        this.stopTimers();
        return;
      }

      const idle = !this.dwellTimer && !this.frame && !this.resumeTimer;
      if (!this.paused && idle) {
        this.scheduleNext();
      }
    },
    restart() {
      this.stopTimers();
      this.paused = false;
      this.remeasure();
    },
    scheduleNext() {
      clearTimeout(this.dwellTimer);
      this.cycleKey += 1;
      this.dwellTimer = setTimeout(() => {
        this.dwellTimer = null;
        this.advance();
      }, this.dwellMs);
    },
    /**
     * Mede antes de andar: um ciclo de atualização pode ter mudado a grade
     * durante a pausa. Da última página, volta ao topo.
     */
    advance() {
      this.offsets = measurePageOffsets(this.$refs.viewport);

      if (!this.running) {
        this.stopTimers();
        return;
      }

      const current = findPageIndex(this.offsets, this.getScrollTop());
      const next = current + 1 < this.pageCount ? current + 1 : 0;

      this.scrollTo(this.offsets[next], () => {
        this.pageIndex = next;
        this.scheduleNext();
      });
    },
    /**
     * Desliza até o destino em `PAGE_TRANSITION_MS`, com aceleração e freio.
     * Quem pediu menos movimento no sistema troca de página sem animação.
     */
    scrollTo(target, done) {
      const start = this.getScrollTop();
      const distance = target - start;
      const canAnimate = typeof window.requestAnimationFrame === 'function';

      if (!distance || !canAnimate || prefersReducedMotion()) {
        this.setScrollTop(target);
        done();
        return;
      }

      const step = (now) => {
        if (this.startedAt === null) {
          this.startedAt = now;
        }

        const progress = (now - this.startedAt) / PAGE_TRANSITION_MS;
        this.setScrollTop(start + distance * easeInOutCubic(progress));

        if (progress < 1) {
          this.frame = window.requestAnimationFrame(step);
          return;
        }

        this.frame = null;
        this.startedAt = null;
        done();
      };

      this.startedAt = null;
      this.frame = window.requestAnimationFrame(step);
    },
    stopTimers() {
      clearTimeout(this.dwellTimer);
      clearTimeout(this.resumeTimer);
      this.dwellTimer = null;
      this.resumeTimer = null;

      if (this.frame && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(this.frame);
      }
      this.frame = null;
      this.startedAt = null;
    },
    /**
     * Roda, toque ou clique: alguém está lendo. A rolagem para e só retoma
     * depois de um tempo sem interação, a partir de onde a pessoa deixou.
     */
    onInteraction() {
      if (!this.running) {
        return;
      }

      this.stopTimers();
      this.paused = true;
      this.resumeTimer = setTimeout(() => {
        this.resumeTimer = null;
        this.paused = false;
        this.remeasure();
      }, RESUME_AFTER_IDLE_MS);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.panel-auto-scroll
  display: flex
  flex: 1
  flex-direction: column
  min-height: 0
  position: relative

  // O respiro da grade mora aqui, e não no corpo da página: é ele que a
  // medição usa para a linha que abre a página parar onde a primeira para.
  &__viewport
    flex: 1
    min-height: 0
    overflow-y: auto
    padding: 0.9rem 1.2rem

  // Celular: a página inteira rola e este container não tem altura própria.
  // Sem rolagem aqui, a Rolagem Automática fica parada por construção.
  +media-max-sm()
    &__viewport
      overflow: visible
      padding: 0.6rem 0.8rem
</style>
